import { useCallback, useEffect, useRef, useState } from "react";
import { canvasToBlob, canvasToDataURL, drawOnContext, lerp } from "../../lib/utils";
import { MAX_REPEATS } from "../../lib/constants";
import { useThread } from "../use-thread";
import l10n from "@src/v3.0.0/lib/l10n";
import { useProduct } from "@src/v3.0.0/store/product";
import { useCropperStore } from "@src/v3.0.0/store/cropper/cropper-store";

// globals
const URL = (window.URL || window.webkitURL);
const imageMimeType = 'image/png';

export const useRepeater = (onUpdate, onError) => {
  const product = useProduct(state => state.product);
  const {preset} = useCropperStore();
  const { createThread } = useThread( require("./worker.js").toString() );
  const $image = useRef(null);
  const $ctx = useRef(document.createElement('canvas').getContext('2d'));
  const $ctx2 = useRef(document.createElement('canvas').getContext('2d'));
  const [image, setImage] = useState(null);
  const $repeater = useRef({
    repeats: preset.repeats,
    isPattern: preset.isPattern
  });

  // console.log('changed repeater');
  const setImageProxy = (src) => {
    // console.trace();
    // console.log('%c setImageProxy', 'background: yellow;');
    setImage(src);
  };

  const onLoadImage = async (e) => {
    try {
      // console.log('onLoadImage');
      setImageProxy(await makeImage());
    } catch(e) {
      onError && onError(e);
    }
  }

  //
  // First time: 
  // the image must be created according to 
  // repeats & isPattern so that 
  // globalHandler logic matches image size
  useEffect(() => {
    if (null === $image.current) {

      const checkIfImageNeedsGapFilling = async (e) => {
        const { target: image } = e;
        const { dimension_width: width, dimension_height: height } = product;
    
        // get the correct image height
        const pRatio = width/height;
        const iHeight = Math.ceil(image.naturalWidth/pRatio);
    
        // get the image height difference
        const {naturalHeight} = image;
        const iDelta = iHeight - naturalHeight;
        
        // console.log('iHeight', iHeight, naturalHeight, image.naturalWidth/naturalHeight, pRatio);

        // iDelta demonstrates that the image differs from the product;
        // the image must be adjusted to match the product's aspect ratio.
        if (iDelta !== 0) {
          $image.current = new Image();
          $image.current.onload = onLoadImage;
          
          // adjust image size
          const ctx = $ctx.current;
          ctx.canvas.width = ctx.width = image.naturalWidth;
          ctx.canvas.height = ctx.height = iHeight;

          if (l10n.wppr_cropper_fill_gap_with_image === "yes") {
            // fill the gap with the spare image portion
            const ctx2 = $ctx2.current;
            ctx2.canvas.width = ctx2.width = image.naturalWidth;
            ctx2.canvas.height = ctx2.height = iDelta;
            ctx2.drawImage(
              image,
              0, image.naturalHeight - iDelta, ctx.width, iDelta, 
              0, 0, ctx2.width, ctx2.height
            );
          }
          // draw the final image
          ctx.drawImage(image, 0, 0);
          // set the image
          $image.current.src = await canvasToDataURL(ctx, imageMimeType);
        } else {
          onLoadImage();
        }
        // console.log('p', width, height, pRatio);
        // console.log('i', image.naturalWidth, image.naturalHeight);
        // console.log('i height', iHeight, iDelta);
      };

      // console.log('create image first time');
      $image.current = new Image();
      $image.current.onload = checkIfImageNeedsGapFilling;
      $image.current.src = product.gui;
      return () => {
        console.log('%c use-repeater->clean()', 'background: cyan');
        $image.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //
  // then on repeats/isPattern change, get the new image
  useEffect(() => {
    if ($image.current) {
      onUpdate && onUpdate();
      if(getStatus()) {
        onLoadImage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.repeats, preset.isPattern]);

  /**
   * makeImage - creates image according to repeats & isPattern
   * @returns {string} image
   */
  const makeImage = async () => {
    if($image.current) {  
      // console.log('post naturalHeight', $image.current.naturalHeight);
      const response = await createThread({
        type: imageMimeType,
        gui: await canvasToBlob(drawOnContext($ctx.current, $image.current), imageMimeType), 
        naturalWidth: $image.current.naturalWidth,
        naturalHeight: $image.current.naturalHeight,
        repeats: preset.repeats,
        isPattern: preset.isPattern,
        qFactor: lerp(1, 0.1, preset.repeats/MAX_REPEATS)
      });
      const objectUrl = URL.createObjectURL(response);
      URL.revokeObjectURL(response);
      return objectUrl;
    } else {
      throw new Error('no image');
    }
  };
  //
  // get repeater status (repeats || isPattern changed)
  const getStatus = useCallback(() => {
    return $repeater.current.repeats !== preset.repeats || $repeater.current.isPattern !== preset.isPattern;
  }, [preset.repeats, preset.isPattern]);

  const save = () => {
    $repeater.current.repeats = preset.repeats;
    $repeater.current.isPattern = preset.isPattern;
  };

  return ({
    image,
    makeImage,
    getStatus,
    save
  });
};  